import {
  ref,
  computed,
  onMounted,
  onUpdated,
  onBeforeUnmount,
  getCurrentInstance,
  useSlots as vueUseSlots
} from 'vue';

type SlotState = Record<string, boolean>;

/**
 * Universal useSlots composable that works in both Nuxt and custom elements.
 * Just call `const slots = useSlots()` and use `!!slots.slotName` in templates.
 */
export const useSlots = () => {
  const instance = getCurrentInstance();
  const states = ref<SlotState>({});
  const cleanup: Array<() => void> = [];

  const isCustomElement = (): boolean => {
    const el = instance?.vnode?.el as Element | null | undefined;
    const root = el?.getRootNode?.();
    return root instanceof ShadowRoot;
  };

  const assignIfChanged = (next: SlotState) => {
    const prev = states.value;
    const prevKeys = Object.keys(prev);
    const nextKeys = Object.keys(next);
    if (prevKeys.length !== nextKeys.length) {
      states.value = next;
      return;
    }
    for (const k of nextKeys) {
      if (prev[k] !== next[k]) {
        states.value = next;
        return;
      }
    }
    // unchanged
  };

  const updateFromCE = () => {
    const el = instance?.vnode?.el as Element | null | undefined;
    const root = el?.getRootNode?.();
    if (!(root instanceof ShadowRoot)) return;

    const slotEls = Array.from(root.querySelectorAll('slot'));
    const next: SlotState = {};

    for (const slotEl of slotEls) {
      const name = slotEl.name || 'default';
      const assigned = slotEl.assignedNodes({ flatten: true });
      next[name] = assigned.length > 0;
    }

    assignIfChanged(next);
  };

  const updateFromVue = () => {
    if (!instance) return;
    const vs = vueUseSlots();
    const next: SlotState = {};

    // Explicit keys present on vs
    for (const name of Object.keys(vs)) {
      try {
        const nodes = vs[name]?.();
        next[name || 'default'] = !!(nodes && nodes.length > 0);
      } catch {
        next[name || 'default'] = false;
      }
    }

    // Ensure default key exists
    if (!('default' in next)) {
      try {
        const nodes = vs.default?.();
        next.default = !!(nodes && nodes.length > 0);
      } catch {
        next.default = false;
      }
    }

    assignIfChanged(next);
  };

  onMounted(() => {
    if (isCustomElement()) {
      updateFromCE();

      // Listen to real slot changes; cheaper than MutationObserver
      const el = instance?.vnode?.el as Element | null | undefined;
      const root = el?.getRootNode?.();
      if (root instanceof ShadowRoot) {
        const slotEls = Array.from(root.querySelectorAll('slot'));
        for (const slotEl of slotEls) {
          const handler = () => updateFromCE();
          slotEl.addEventListener('slotchange', handler);
          cleanup.push(() => slotEl.removeEventListener('slotchange', handler));
        }
      }
    } else {
      updateFromVue();
    }
  });

  // In non-CE mode, slot content can change across updates
  onUpdated(() => {
    if (!isCustomElement()) {
      updateFromVue();
    }
  });

  onBeforeUnmount(() => {
    for (const fn of cleanup) fn();
  });

  return computed(() => states.value);
};
